import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import InsightButton from "../components/InsightButton";
import { RichiamoScadenzeContrattuali } from "../components/ScadenzePersonaleWidget";
import VerifichePeriodicheTab from "../components/VerifichePeriodicheTab";
import { confirm } from "../utils/confirm";
import { esportaXLSX } from "../utils/esportaXLSX";
import { collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { useCanSee } from "../hooks/useCanSee";
import { useToast } from "../utils/Toast";
import { Plus, X, Clock, Car, Shield, Check, GripVertical, Users, Monitor, Wrench, FileText, Settings } from "lucide-react";
import { toLocalDateStr } from "../utils/dateUtils";
import { prossimaScadenza, prossimaScadenzaCapitolato, prossimaScadenzaVoce, FREQUENZE } from "../components/ManutenzioneTab";

// ─── UTILITIES ──────────────────────────────────────────────────────────
function giorniAllaScadenza(dataStr) {
  if (!dataStr) return null;
  const oggi = new Date(); oggi.setHours(0,0,0,0);
  const scad = new Date(dataStr+"T00:00:00");
  return Math.round((scad - oggi)/(1000*60*60*24));
}

function BadgeScadenza({ data }) {
  if (!data) return <span style={{color:"#d1d5db",fontSize:11}}>—</span>;
  const g = giorniAllaScadenza(data);
  const fmt = new Date(data+"T00:00:00").toLocaleDateString("it-IT",{day:"numeric",month:"short",year:"numeric"});
  let col="#059669",bg="#d1fae5",label=fmt;
  if (g<0)       { col="#dc2626"; bg="#fee2e2"; label=`SCADUTA da ${Math.abs(g)}gg`; }
  else if(g===0) { col="#dc2626"; bg="#fee2e2"; label="Scade OGGI"; }
  else if(g<=30) { col="#d97706"; bg="#fff7ed"; label=`${fmt} (${g}gg)`; }
  return <span style={{fontSize:11,fontWeight:700,color:col,background:bg,padding:"2px 8px",borderRadius:10,whiteSpace:"nowrap"}}>{label}</span>;
}

const RICORRENZE = ["Nessuna","Mensile","Bimestrale","Trimestrale","Semestrale","Annuale","Biennale","Triennale","Quinquennale","Prima dell'inizio cantiere","Km","Personalizzata"];

// Calcola prossima scadenza in base alla ricorrenza
function calcolaProssimaScadenza(dataStr, ricorrenza) {
  if (!dataStr || ricorrenza==="Nessuna" || ricorrenza==="Prima dell'inizio cantiere" || ricorrenza==="Km" || ricorrenza==="Personalizzata") return null;
  const d = new Date(dataStr+"T00:00:00");
  const mesi = { Mensile:1, Bimestrale:2, Trimestrale:3, Semestrale:6, Annuale:12, Biennale:24, Triennale:36, Quinquennale:60 };
  const m = mesi[ricorrenza];
  if (!m) return null;
  d.setMonth(d.getMonth()+m);
  return toLocalDateStr(d);
}

// ─── CELLA INLINE ───────────────────────────────────────────────────────
function CellaInline({ value, type="text", options, onSave, onCancel, placeholder="" }) {
  const [val, setVal] = useState(value||"");
  const ref = useRef();
  useEffect(()=>{ ref.current?.focus(); },[]);
  const save = ()=>onSave(val);
  const handleKey = e=>{ if(e.key==="Enter") save(); if(e.key==="Escape") onCancel(); };

  if (options) return (
    <select ref={ref} value={val}
      onChange={e=>{ setVal(e.target.value); onSave(e.target.value); }}
      onBlur={onCancel}
      style={{width:"100%",padding:"3px 5px",border:"2px solid #7c3aed",borderRadius:5,fontSize:12,fontFamily:"'Inter',sans-serif",outline:"none"}}>
      {options.map(o=><option key={o.value||o} value={o.value||o}>{o.label||o}</option>)}
    </select>
  );

  return (
    <div style={{display:"flex",gap:3,alignItems:"center"}}>
      <input ref={ref} type={type} value={val} onChange={e=>setVal(e.target.value)}
        onBlur={save} onKeyDown={handleKey} placeholder={placeholder}
        style={{flex:1,padding:"3px 5px",border:"2px solid #7c3aed",borderRadius:5,fontSize:12,fontFamily:"'Inter',sans-serif",outline:"none",minWidth:0}}/>
      <button onMouseDown={e=>{e.preventDefault();save();}}
        style={{background:"#7c3aed",border:"none",borderRadius:4,padding:"3px 5px",cursor:"pointer",color:"#fff",flexShrink:0}}>
        <Check size={10}/>
      </button>
    </div>
  );
}

function Cella({ value, display, canEdit, onEdit, placeholder="—" }) {
  return (
    <div onClick={canEdit?onEdit:undefined}
      style={{padding:"3px 5px",minHeight:26,cursor:canEdit?"pointer":"default",
        color:value?"#111827":"#d1d5db",fontSize:12,borderRadius:4}}
      onMouseOver={e=>{if(canEdit)e.currentTarget.style.background="#f3f4f6";}}
      onMouseOut={e=>{e.currentTarget.style.background="transparent";}}>
      {display||value||placeholder}
    </div>
  );
}

// ─── VOCI PREDEFINITE ────────────────────────────────────────────────────
const VOCI_SICUREZZA = ["DURC","POS (Piano Operativo di Sicurezza)","Visura Camerale","Formazione Specifica","Assicurazione Aziendale","DVR (Documento Valutazione Rischi)","DUVRI (a cura dell'appaltante)","Idoneità Sanitaria","Consegna DPI","Verbale DPI","PSC (Piano Sicurezza Coordinamento)","Altro"];
const VOCI_AUTO = ["Assicurazione","Revisione","Tagliando","Rata Finanziaria/Leasing","Bollo","Altro"];
const VOCI_PERSONALE = ["Visita medica","Formazione sicurezza","Corso PES/PAV/PEI","Patentino PLE","Patentino Carrello elevatore","Patente","CQC","Carta Tachigrafica","DPI in scadenza","Contratto dipendente","Periodo di prova","Certificazioni professionali","Altro"];
const VOCI_CONTATORI = [
  "Verifica contatore di scambio",
  "Verifica contatore di produzione",
];

const VOCI_LICENZE = ["Dominio Internet","PEC","Firma digitale","SPID aziendale","Hosting","SSL","Microsoft 365","Google Workspace","Adobe","Antivirus","Licenze CAD","Altro"];
const VOCI_IMMOBILI = ["Contratto di locazione","Manutenzione climatizzatori","Antincendio","Estintori","Ascensori","Impianti elettrici","Pulizie programmate","Altro"];
const VOCI_AMMINISTRAZIONE = ["F24","IVA","Bilancio","DURC","Polizze","Rate","Leasing","Canoni","Altro"];



// ─── TAB PARCO AUTO (logica speciale: aggiunta per veicolo) ─────────────
function AutoTab({ orgId, utenti, canEdit }) {
  const { addToast } = useToast();
  const [voci, setVoci] = useState([]);
  const [cellAttiva, setCellAttiva] = useState(null);
  const [showNuovoMezzo, setShowNuovoMezzo] = useState(false);
  const [nuovoMezzo, setNuovoMezzo] = useState({ targa:"", descrizione:"" });
  const [search, setSearch] = useState("");
  const [filtroScadenza, setFiltroScadenza] = useState(""); // "" | "scadute" | "urgenti"

  useEffect(()=>{
    if(!orgId) return;
    const q=query(collection(db,"scadenzario"),where("organizationId","==",orgId),where("tipo","==","auto"));
    return onSnapshot(q,snap=>{
      const l=snap.docs.map(d=>({id:d.id,...d.data()}));
      l.sort((a,b)=>(a.targa||"").localeCompare(b.targa||"")||(a.ordine??999)-(b.ordine??999));
      setVoci(l);
    });
  },[orgId]);

  const targheEsistenti = useMemo(()=>[...new Set(voci.map(v=>v.targa?.toUpperCase()).filter(Boolean))],[voci]);

  const aggiungiMezzo = async () => {
    const targaClean = nuovoMezzo.targa.trim().toUpperCase().replace(/\s/g,"");
    if (!targaClean) { addToast("Targa obbligatoria","error"); return; }
    if (targheEsistenti.includes(targaClean)) { addToast(`Targa ${targaClean} già presente`,"error"); return; }
    // Crea automaticamente tutte le voci predefinite per questo mezzo
    for (let i=0;i<VOCI_AUTO.length;i++) {
      await addDoc(collection(db,"scadenzario"),{
        nome:VOCI_AUTO[i], tipo:"auto", organizationId:orgId,
        targa:targaClean, descrizione:nuovoMezzo.descrizione.trim(),
        dataScadenza:"", ricorrenza:"Nessuna",
        promemoria:true, promemoriaGiorni:30,
        assegnatoA:"", assegnatoNome:"", note:"",
        completata:false, ordine:i,
        createdAt:serverTimestamp(), updatedAt:serverTimestamp(),
      });
    }
    setNuovoMezzo({ targa:"", descrizione:"" });
    setShowNuovoMezzo(false);
    addToast(`Mezzo ${targaClean} aggiunto con tutte le voci ✅`,"success");
  };

  const aggiornaVoce = async (id, campo, valore) => {
    await updateDoc(doc(db,"scadenzario",id),{[campo]:valore,updatedAt:serverTimestamp()});
    setCellAttiva(null);
  };

  const eliminaMezzo = async (targa) => {
    if (!await confirm(`Eliminare il mezzo ${targa} e tutte le sue voci?`, { type:"danger" })) return;
    const daEliminare = voci.filter(v=>v.targa===targa);
    for (const v of daEliminare) await deleteDoc(doc(db,"scadenzario",v.id));
    addToast("Mezzo eliminato","info");
  };

  const aggiungiVoceLibera = async (targa, descrizione) => {
    await addDoc(collection(db,"scadenzario"),{
      nome:"Nuova voce", tipo:"auto", organizationId:orgId,
      targa, descrizione, dataScadenza:"", ricorrenza:"Nessuna",
      promemoria:true, promemoriaGiorni:30,
      assegnatoA:"", assegnatoNome:"", note:"",
      completata:false, ordine:voci.filter(v=>v.targa===targa).length,
      createdAt:serverTimestamp(), updatedAt:serverTimestamp(),
    });
  };

  const completaVoce = async (voce) => {
    if(voce.eseguita){
      await updateDoc(doc(db,"scadenzario",voce.id),{ eseguita:false, updatedAt:serverTimestamp() });
    } else {
      const prossima = calcolaProssimaScadenza(voce.dataScadenza, voce.ricorrenza);
      await updateDoc(doc(db,"scadenzario",voce.id),{
        eseguita:true, dataCompletamento:toLocalDateStr(new Date()),
        dataScadenzaPrecedente:voce.dataScadenza, dataScadenza:prossima||voce.dataScadenza,
        updatedAt:serverTimestamp(),
      });
      if(prossima) addToast(`Scadenza rinnovata al ${new Date(prossima+"T00:00:00").toLocaleDateString("it-IT")}`,"success");
    }
  };

  const elimina = async id => { if (!await confirm("Eliminare voce?", { type:"danger" })) return; await deleteDoc(doc(db,"scadenzario",id)); };

  // Raggruppa per targa
  const targhe = useMemo(()=>{
    const m={};
    voci.forEach(v=>{
      const k=v.targa||"__";
      if(!m[k]) m[k]={ targa:v.targa, descrizione:v.descrizione, voci:[] };
      m[k].voci.push(v);
    });
    return Object.values(m)
      .filter(g=>!search||g.targa?.toLowerCase().includes(search.toLowerCase())||g.descrizione?.toLowerCase().includes(search.toLowerCase()))
      .filter(g=>{
        if(!filtroScadenza) return true;
        return g.voci.some(v=>{
          const gg=giorniAllaScadenza(v.dataScadenza);
          if(gg===null) return false;
          return filtroScadenza==="scadute" ? gg<0 : (gg>=0&&gg<=30);
        });
      });
  },[voci,search,filtroScadenza]);

  const scadute = voci.filter(v=>{ const g=giorniAllaScadenza(v.dataScadenza); return g!==null&&g<0; }).length;
  const urgenti = voci.filter(v=>{ const g=giorniAllaScadenza(v.dataScadenza); return g!==null&&g>=0&&g<=30; }).length;

  const COLONNE = [
    {key:"nome",        label:"Tipo voce",        width:160},
    {key:"assegnatoA",  label:"Assegnato a",      width:120, special:"utente"},
    {key:"dataScadenza",label:"Scadenza attuale",  width:150, type:"date"},
    {key:"ricorrenza",  label:"Ricorrenza",        width:120, options:RICORRENZE.map(r=>({value:r,label:r}))},
    {key:"promemoria",  label:"Promemoria",        width:90,  special:"promemoria"},
    {key:"__prossima",  label:"Prossima scadenza", width:150, special:"prossima"},
    {key:"__promProssima",label:"Prom. prossima",  width:100, special:"promProssima"},
    {key:"note",        label:"Note",              width:150},
    {key:"__completa",  label:"",                  width:36,  special:"completa"},
  ];

  const renderCella = (voce, col) => {
    const isAttiva = cellAttiva?.id===voce.id&&cellAttiva?.campo===col.key;
    let display=null, value=voce[col.key]||"";

    if(col.key==="dataScadenza"){ display=<BadgeScadenza data={voce.dataScadenza}/>; value=voce.dataScadenza||""; }
    if(col.key==="nome") display=<span style={{fontWeight:600}}>{voce.nome}</span>;
    if(col.special==="utente"){ display=voce.assegnatoNome?<span style={{fontSize:11,color:"#6b7280"}}>👤 {voce.assegnatoNome}</span>:null; value=voce.assegnatoNome||""; }
    if(col.special==="promemoria") display=voce.promemoria?<span style={{fontSize:11,color:"#0891b2"}}>🔔 {voce.promemoriaGiorni}gg</span>:<span style={{color:"#d1d5db",fontSize:11}}>—</span>;
    if(col.key==="ricorrenza"&&voce.ricorrenza&&voce.ricorrenza!=="Nessuna") display=<span style={{fontSize:11,color:"#7c3aed",background:"#ede9fe",padding:"2px 6px",borderRadius:8}}>🔄 {voce.ricorrenza}</span>;
    if(col.special==="prossima") {
      const prossima=calcolaProssimaScadenza(voce.dataScadenza,voce.ricorrenza);
      if(!prossima) return <span style={{color:"#d1d5db",fontSize:11}}>—</span>;
      return <span style={{fontSize:11,fontWeight:700,color:"#7c3aed",background:"#ede9fe",padding:"2px 8px",borderRadius:8,whiteSpace:"nowrap"}}>📅 {new Date(prossima+"T00:00:00").toLocaleDateString("it-IT")}</span>;
    }
    if(col.special==="promProssima") {
      const isAttP=cellAttiva?.id===voce.id&&cellAttiva?.campo==="promProssima";
      const valP=voce.promemoriaGiorniProssima||voce.promemoriaGiorni||30;
      if(isAttP) return <CellaInline value={String(valP)} type="number" onSave={v=>aggiornaVoce(voce.id,"promemoriaGiorniProssima",parseInt(v)||30)} onCancel={()=>setCellAttiva(null)}/>;
      return <div onClick={canEdit?()=>setCellAttiva({id:voce.id,campo:"promProssima"}):undefined} style={{cursor:canEdit?"pointer":"default",padding:"3px 5px",borderRadius:4}} onMouseOver={e=>{if(canEdit)e.currentTarget.style.background="#f3f4f6";}} onMouseOut={e=>{e.currentTarget.style.background="transparent";}}><span style={{fontSize:11,color:"#0891b2"}}>🔔 {valP}gg prima</span></div>;
    }
    if(col.special==="completa") return (
      <button onClick={()=>completaVoce(voce)} title="Segna completata e genera nuova scadenza"
        style={{
          width:26, height:26, borderRadius:"50%", border:"2px solid",
          cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center",
          flexShrink:0, transition:"all .15s",
          background: giorniAllaScadenza(voce.dataScadenza)<0 && !voce.eseguita ? "#dc2626" :
                      voce.eseguita ? "#059669" : "#374151",
          borderColor: giorniAllaScadenza(voce.dataScadenza)<0 && !voce.eseguita ? "#dc2626" :
                       voce.eseguita ? "#059669" : "#374151",
        }}>
        <span style={{color:"#fff",fontSize:13,lineHeight:1}}>
          {voce.eseguita ? "✓" : giorniAllaScadenza(voce.dataScadenza)<0 ? "!" : "●"}
        </span>
      </button>
    );

    if(isAttiva){
      if(col.special==="promemoria") return (
        <div style={{display:"flex",alignItems:"center",gap:5}}>
          <input type="checkbox" checked={!!voce.promemoria} onChange={e=>aggiornaVoce(voce.id,"promemoria",e.target.checked)} style={{accentColor:"#7c3aed",width:14,height:14}}/>
          {voce.promemoria&&<CellaInline value={String(voce.promemoriaGiorni||30)} type="number" onSave={v=>aggiornaVoce(voce.id,"promemoriaGiorni",parseInt(v)||30)} onCancel={()=>setCellAttiva(null)}/>}
        </div>
      );
      if(col.key==="dataScadenza") return (
        <div>
          <CellaInline value={value} type="date"
            onSave={v=>aggiornaVoce(voce.id,col.key,v)}
            onCancel={()=>setCellAttiva(null)}/>
          <div style={{fontSize:10,color:"#9ca3af",marginTop:2}}>formato gg/mm/aaaa</div>
        </div>
      );
      const opts=col.special==="utente"?[{value:"",label:"Nessuno"},...utenti.map(u=>({value:u.uid,label:u.displayName}))]:col.options||null;
      return (
        <CellaInline value={value} type={col.type||"text"} options={opts}
          onSave={v=>{ if(col.special==="utente"){const u=utenti.find(u=>u.uid===v);aggiornaVoce(voce.id,"assegnatoA",v);aggiornaVoce(voce.id,"assegnatoNome",u?.displayName||"");}else aggiornaVoce(voce.id,col.key,v); }}
          onCancel={()=>setCellAttiva(null)}/>
      );
    }
    return <Cella value={value} display={display} canEdit={canEdit} onEdit={()=>setCellAttiva({id:voce.id,campo:col.key})}/>;
  };

  return (
    <div style={{padding:20}}>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:8}}>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          {scadute>0&&<button onClick={()=>setFiltroScadenza(f=>f==="scadute"?"":"scadute")} style={{fontSize:11,fontWeight:700,color:"#dc2626",background:filtroScadenza==="scadute"?"#fca5a5":"#fee2e2",padding:"2px 8px",borderRadius:10,border:"1px solid "+(filtroScadenza==="scadute"?"#dc2626":"transparent"),cursor:"pointer"}}>⚠️ {scadute} {scadute===1?"scaduta":"scadute"}</button>}
          {urgenti>0&&<button onClick={()=>setFiltroScadenza(f=>f==="urgenti"?"":"urgenti")} style={{fontSize:11,fontWeight:700,color:"#d97706",background:filtroScadenza==="urgenti"?"#fdba74":"#fff7ed",padding:"2px 8px",borderRadius:10,border:"1px solid "+(filtroScadenza==="urgenti"?"#d97706":"transparent"),cursor:"pointer"}}>🔔 {urgenti} in scadenza</button>}
          <span style={{fontSize:12,color:"#9ca3af"}}>{targhe.length} mezzi · {voci.length} voci</span>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <div style={{position:"relative"}}>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍 Targa o descrizione..."
              style={{padding:"6px 26px 6px 10px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:12,fontFamily:"'Inter',sans-serif",outline:"none",width:180}}/>
            {search&&<button onClick={()=>setSearch("")} style={{position:"absolute",right:6,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",color:"#9ca3af",fontSize:14}}>×</button>}
          </div>
          {canEdit&&(
            <button onClick={()=>setShowNuovoMezzo(true)}
              style={{padding:"7px 16px",border:"none",borderRadius:8,background:"#7c3aed",color:"#fff",
                fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",gap:6}}>
              🚗 Aggiungi auto/furgone
            </button>
          )}
        </div>
      </div>

      {/* Modal nuovo mezzo */}
      {showNuovoMezzo && (
        <div style={{background:"#f0f9ff",border:"1px solid #bae6fd",borderRadius:12,padding:16,marginBottom:20}}>
          <div style={{fontSize:13,fontWeight:700,color:"#0369a1",marginBottom:10}}>🚗 Nuovo veicolo — verranno create automaticamente tutte le voci</div>
          <div style={{display:"grid",gridTemplateColumns:"120px 1fr auto",gap:10,alignItems:"flex-end"}}>
            <div>
              <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Targa *</label>
              <input autoFocus value={nuovoMezzo.targa} onChange={e=>setNuovoMezzo(f=>({...f,targa:e.target.value}))}
                placeholder="AB123CD" style={{width:"100%",padding:"7px 10px",border:"1px solid #e5e7eb",borderRadius:7,fontSize:13,fontFamily:"'Inter',sans-serif",outline:"none",textTransform:"uppercase",boxSizing:"border-box"}}
                onKeyDown={e=>e.key==="Enter"&&aggiungiMezzo()}/>
            </div>
            <div>
              <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Descrizione (es. Fiat Ducato bianco)</label>
              <input value={nuovoMezzo.descrizione} onChange={e=>setNuovoMezzo(f=>({...f,descrizione:e.target.value}))}
                placeholder="Es. Fiat Ducato bianco 2022" style={{width:"100%",padding:"7px 10px",border:"1px solid #e5e7eb",borderRadius:7,fontSize:13,fontFamily:"'Inter',sans-serif",outline:"none",boxSizing:"border-box"}}
                onKeyDown={e=>e.key==="Enter"&&aggiungiMezzo()}/>
            </div>
            <div style={{display:"flex",gap:6}}>
              <button onClick={aggiungiMezzo} style={{padding:"7px 16px",background:"#7c3aed",color:"#fff",border:"none",borderRadius:7,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif",whiteSpace:"nowrap"}}>Crea mezzo</button>
              <button onClick={()=>setShowNuovoMezzo(false)} style={{padding:"7px 12px",background:"#f3f4f6",color:"#6b7280",border:"none",borderRadius:7,fontSize:13,cursor:"pointer"}}>✕</button>
            </div>
          </div>
          <div style={{fontSize:11,color:"#0369a1",marginTop:8}}>
            Verranno create automaticamente: {VOCI_AUTO.join(", ")}
          </div>
        </div>
      )}

      {/* Gruppi per targa */}
      {targhe.length===0 ? (
        <div style={{textAlign:"center",padding:48,color:"#9ca3af"}}>
          <span style={{fontSize:40}}>🚗</span>
          <div style={{fontSize:14,fontWeight:600,marginTop:12}}>Nessun veicolo ancora</div>
          <div style={{fontSize:12,marginTop:4}}>Clicca "Aggiungi auto/furgone" per iniziare</div>
        </div>
      ) : targhe.map(gruppo=>{
        const scaduteGruppo=gruppo.voci.filter(v=>{ const g=giorniAllaScadenza(v.dataScadenza); return g!==null&&g<0; }).length;
        const urgentiGruppo=gruppo.voci.filter(v=>{ const g=giorniAllaScadenza(v.dataScadenza); return g!==null&&g>=0&&g<=30; }).length;
        return (
          <div key={gruppo.targa||"__"} style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:12,marginBottom:16,overflow:"hidden"}}>
            {/* Header gruppo */}
            <div style={{display:"flex",alignItems:"center",gap:10,padding:"12px 16px",background:"#f9fafb",borderBottom:"1px solid #e5e7eb"}}>
              <span style={{fontSize:18}}>🚗</span>
              {gruppo.targa&&<span style={{fontSize:14,fontWeight:800,color:"#fff",background:"#374151",padding:"3px 12px",borderRadius:6,fontFamily:"monospace"}}>{gruppo.targa}</span>}
              {gruppo.descrizione&&<span style={{fontSize:13,color:"#6b7280",fontWeight:500}}>{gruppo.descrizione}</span>}
              {scaduteGruppo>0&&<span style={{fontSize:11,fontWeight:700,color:"#dc2626",background:"#fee2e2",padding:"2px 7px",borderRadius:8}}>⚠️ {scaduteGruppo}</span>}
              {urgentiGruppo>0&&<span style={{fontSize:11,fontWeight:700,color:"#d97706",background:"#fff7ed",padding:"2px 7px",borderRadius:8}}>🔔 {urgentiGruppo}</span>}
              <div style={{flex:1}}/>
              {canEdit&&(
                <>
                  <button onClick={()=>aggiungiVoceLibera(gruppo.targa,gruppo.descrizione)}
                    style={{fontSize:11,color:"#7c3aed",background:"#ede9fe",border:"none",borderRadius:6,padding:"3px 10px",cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                    + Voce libera
                  </button>
                  <button onClick={()=>eliminaMezzo(gruppo.targa)}
                    style={{fontSize:11,color:"#dc2626",background:"none",border:"1px solid #fca5a5",borderRadius:6,padding:"3px 8px",cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                    Elimina mezzo
                  </button>
                </>
              )}
            </div>
            {/* Tabella voci del mezzo */}
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:12,minWidth:700}}>
                <thead>
                  <tr style={{background:"#fafafa",borderBottom:"1px solid #f3f4f6"}}>
                    {COLONNE.map(col=>(
                      <th key={col.key} style={{padding:"6px 10px",textAlign:"left",fontSize:10,fontWeight:700,color:"#9ca3af",whiteSpace:"nowrap",minWidth:col.width}}>{col.label}</th>
                    ))}
                    <th style={{width:30}}/>
                  </tr>
                </thead>
                <tbody>
                  {gruppo.voci.map((voce,ri)=>{
                    const g=giorniAllaScadenza(voce.dataScadenza);
                    const isScaduta=g!==null&&g<0;
                    const isUrgente=g!==null&&g>=0&&g<=30;
                    return (
                      <tr key={voce.id} style={{borderBottom:"1px solid #f9fafb",background:isScaduta?"#fff5f5":isUrgente?"#fffbeb":ri%2===0?"#fff":"#fafafa"}}>
                        {COLONNE.map(col=>(
                          <td key={col.key} style={{padding:"4px 8px",verticalAlign:"middle"}}>{renderCella(voce,col)}</td>
                        ))}
                        <td style={{padding:"4px 6px"}}>
                          {canEdit&&<button onClick={()=>elimina(voce.id)} style={{background:"none",border:"none",cursor:"pointer",color:"#d1d5db",padding:3}} onMouseOver={e=>e.currentTarget.style.color="#dc2626"} onMouseOut={e=>e.currentTarget.style.color="#d1d5db"}><X size={11}/></button>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── TAB GENERICO CON DRAG&DROP ──────────────────────────────────────────
function ScadenzarioTab({ tipo, vociPredefinite, orgId, utenti, canEdit, ricorrenzaDefault="Nessuna" }) {
  const { addToast } = useToast();
  const [voci, setVoci] = useState([]);
  const [cellAttiva, setCellAttiva] = useState(null);
  const [search, setSearch] = useState("");
  const [filtroScadenza, setFiltroScadenza] = useState(""); // "" | "scadute" | "urgenti"
  const [predefiniteAggiunte, setPredefiniteAggiunte] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  useEffect(()=>{
    if (!orgId) return;
    const q = query(collection(db,"scadenzario"), where("organizationId","==",orgId), where("tipo","==",tipo));
    return onSnapshot(q, snap=>{
      const l = snap.docs.map(d=>({id:d.id,...d.data()}));
      l.sort((a,b)=>(a.ordine??999)-(b.ordine??999));
      setVoci(l);
      if(l.length>0) setPredefiniteAggiunte(true);
    });
  },[orgId,tipo]);

  const aggiornaVoce = async (id, campo, valore) => {
    if (campo==="targa"&&valore?.trim()) {
      const dup = voci.find(v=>v.id!==id&&v.targa?.toUpperCase()===valore.trim().toUpperCase());
      if(dup){ addToast(`Targa già presente: ${dup.nome}`,"error"); setCellAttiva(null); return; }
    }
    await updateDoc(doc(db,"scadenzario",id),{ [campo]:campo==="targa"?valore.trim().toUpperCase():valore, updatedAt:serverTimestamp() });
    setCellAttiva(null);
  };

  const aggiungiVoce = async (nome="Nuova voce") => {
    const ordine = voci.length;
    await addDoc(collection(db,"scadenzario"),{
      nome, tipo, organizationId:orgId,
      targa:"", descrizione:"", dataScadenza:"",
      ricorrenza:"Nessuna", promemoria:true, promemoriaGiorni:30,
      assegnatoA:"", assegnatoNome:"", note:"",
      completata:false, ordine,
      createdAt:serverTimestamp(), updatedAt:serverTimestamp(),
    });
  };

  const aggiungiTuttePredefinite = async () => {
    const nomiEsistenti = voci.map(v=>v.nome);
    const mancanti = vociPredefinite.filter(n=>!nomiEsistenti.includes(n));
    for (let i=0;i<mancanti.length;i++) {
      await addDoc(collection(db,"scadenzario"),{
        nome:mancanti[i], tipo, organizationId:orgId,
        targa:"", descrizione:"", dataScadenza:"",
        ricorrenza:"Nessuna", promemoria:true, promemoriaGiorni:30,
        assegnatoA:"", assegnatoNome:"", note:"",
        completata:false, ordine:voci.length+i,
        createdAt:serverTimestamp(), updatedAt:serverTimestamp(),
      });
    }
    setPredefiniteAggiunte(true);
    addToast(mancanti.length>0?`Aggiunte ${mancanti.length} voci ✅`:"Già tutte presenti","success");
  };

  // Completa voce e genera prossima scadenza
  const completaVoce = async (voce) => {
    // Rosso (scaduta) → click → torna nero (aggiorna scadenza al prossimo ciclo)
    const prossima = calcolaProssimaScadenza(voce.dataScadenza, voce.ricorrenza);
    await updateDoc(doc(db,"scadenzario",voce.id),{
      dataCompletamento: toLocalDateStr(new Date()),
      dataScadenzaPrecedente: voce.dataScadenza,
      dataScadenza: prossima||voce.dataScadenza,
      updatedAt: serverTimestamp(),
    });
    addToast(prossima?`Scadenza rinnovata al ${new Date(prossima+"T00:00:00").toLocaleDateString("it-IT")}`:"Eseguita ✅","success");
  };

  const eliminaVoce = async id => {
    if (!await confirm("Eliminare?", { type:"danger" })) return;
    await deleteDoc(doc(db,"scadenzario",id));
  };

  // Drag & drop
  const onDragStart = (e,i) => { setDragIdx(i); e.dataTransfer.effectAllowed="move"; };
  const onDragOver = (e,i) => { e.preventDefault(); setDragOverIdx(i); };
  const onDrop = async (e,i) => {
    e.preventDefault();
    if (dragIdx===null||dragIdx===i) { setDragIdx(null); setDragOverIdx(null); return; }
    const nuova = [...voci];
    const [spostata] = nuova.splice(dragIdx,1);
    nuova.splice(i,0,spostata);
    setDragIdx(null); setDragOverIdx(null);
    for (let j=0;j<nuova.length;j++) {
      await updateDoc(doc(db,"scadenzario",nuova[j].id),{ordine:j,updatedAt:serverTimestamp()});
    }
  };

  const filtrate = voci.filter(v=>{
    if (v.completata) return false; // nasconde voci completate
    if (!search) return true;
    return v.nome?.toLowerCase().includes(search.toLowerCase()) ||
      v.targa?.toLowerCase().includes(search.toLowerCase()) ||
      v.assegnatoNome?.toLowerCase().includes(search.toLowerCase());
  });

  const scadute = filtrate.filter(v=>{ const g=giorniAllaScadenza(v.dataScadenza); return g!==null&&g<0; }).length;
  const urgenti = filtrate.filter(v=>{ const g=giorniAllaScadenza(v.dataScadenza); return g!==null&&g>=0&&g<=30; }).length;
  const filtrateVisibili = filtrate.filter(v=>{
    if(!filtroScadenza) return true;
    const g=giorniAllaScadenza(v.dataScadenza);
    if(g===null) return false;
    return filtroScadenza==="scadute" ? g<0 : (g>=0&&g<=30);
  });

  const opzioniNome = predefiniteAggiunte ? null : [
    {value:"",label:"— Seleziona o scrivi —"},
    ...vociPredefinite.map(n=>({value:n,label:n})),
  ];

  const COLONNE_BASE = [
    {key:"nome",        label:"Voce / Documento",  width:200},
    ...(tipo==="auto"?[{key:"targa",label:"Targa *",width:100},{key:"descrizione",label:"Descrizione",width:150}]:[]),
    ...(tipo==="personale"?[{key:"descrizione",label:"Nominativo/Note",width:150}]:[]),
    {key:"assegnatoA",  label:"Assegnato a",       width:130, special:"utente"},
    {key:"dataScadenza",label:"Scadenza",           width:140},
    {key:"ricorrenza",  label:"Ricorrenza",         width:130, options:RICORRENZE.map(r=>({value:r,label:r}))},
    {key:"promemoria",  label:"Promemoria",         width:100, special:"promemoria"},
    {key:"note",        label:"Note",               width:160},
    {key:"__completa",  label:"✓",                  width:50,  special:"completa"},
  ];

  const renderCella = (voce, col) => {
    const isAttiva = cellAttiva?.id===voce.id&&cellAttiva?.campo===col.key;
    let display=null, value=voce[col.key]||"";

    if (col.key==="dataScadenza") { display=<BadgeScadenza data={voce.dataScadenza}/>; value=voce.dataScadenza||""; }
    if (col.key==="targa") display=voce.targa?<span style={{fontFamily:"monospace",fontWeight:700}}>{voce.targa}</span>:null;
    if (col.key==="nome") display=<span style={{fontWeight:600,color:"#111827"}}>{voce.nome||<span style={{color:"#d1d5db"}}>—</span>}</span>;
    if (col.special==="utente") { display=voce.assegnatoNome?<span style={{fontSize:11,color:"#6b7280"}}>👤 {voce.assegnatoNome}</span>:null; value=voce.assegnatoNome||""; }
    if (col.special==="promemoria") display=voce.promemoria?<span style={{fontSize:11,color:"#0891b2"}}>🔔 {voce.promemoriaGiorni}gg</span>:<span style={{color:"#d1d5db",fontSize:11}}>—</span>;
    if (col.key==="ricorrenza"&&voce.ricorrenza&&voce.ricorrenza!=="Nessuna") display=<span style={{fontSize:11,color:"#7c3aed",background:"#ede9fe",padding:"2px 6px",borderRadius:8}}>🔄 {voce.ricorrenza}</span>;
    if (col.special==="prossima") {
      const prossima = calcolaProssimaScadenza(voce.dataScadenza, voce.ricorrenza);
      if (!prossima) return <span style={{color:"#d1d5db",fontSize:11}}>—</span>;
      return (
        <div>
          <span style={{fontSize:11,fontWeight:700,color:"#7c3aed",background:"#ede9fe",padding:"2px 8px",borderRadius:8,whiteSpace:"nowrap"}}>
            📅 {new Date(prossima+"T00:00:00").toLocaleDateString("it-IT")}
          </span>
        </div>
      );
    }
    if (col.special==="promProssima") {
      const isAttivaProm = cellAttiva?.id===voce.id && cellAttiva?.campo==="promProssima";
      const valProm = voce.promemoriaGiorniProssima||voce.promemoriaGiorni||30;
      if (isAttivaProm) return (
        <CellaInline value={String(valProm)} type="number"
          onSave={v=>aggiornaVoce(voce.id,"promemoriaGiorniProssima",parseInt(v)||30)}
          onCancel={()=>setCellAttiva(null)}/>
      );
      return (
        <div onClick={canEdit?()=>setCellAttiva({id:voce.id,campo:"promProssima"}):undefined}
          style={{cursor:canEdit?"pointer":"default",padding:"3px 5px",borderRadius:4}}
          onMouseOver={e=>{if(canEdit)e.currentTarget.style.background="#f3f4f6";}}
          onMouseOut={e=>{e.currentTarget.style.background="transparent";}}>
          <span style={{fontSize:11,color:"#0891b2"}}>🔔 {valProm}gg prima</span>
        </div>
      );
    }
    if (col.special==="completa") return (
      <button onClick={()=>completaVoce(voce)} title={giorniAllaScadenza(voce.dataScadenza)<0?"Clicca: segna come eseguita e rinnova scadenza":"Da fare"}
        style={{width:22,height:22,borderRadius:"50%",border:"2px solid",cursor:"pointer",
          display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"all .15s",
          background:giorniAllaScadenza(voce.dataScadenza)<0?"#dc2626":"#374151",
          borderColor:giorniAllaScadenza(voce.dataScadenza)<0?"#dc2626":"#374151"}}>
        <span style={{color:"#fff",fontSize:10,fontWeight:700,lineHeight:1}}>
          {giorniAllaScadenza(voce.dataScadenza)<0?"!":"●"}
        </span>
      </button>
    );

    if (isAttiva) {
      if (col.special==="promemoria") return (
        <div style={{display:"flex",alignItems:"center",gap:5}}>
          <input type="checkbox" checked={!!voce.promemoria}
            onChange={e=>aggiornaVoce(voce.id,"promemoria",e.target.checked)}
            style={{accentColor:"#7c3aed",width:14,height:14}}/>
          {voce.promemoria && <CellaInline value={String(voce.promemoriaGiorni||30)} type="number"
            onSave={v=>aggiornaVoce(voce.id,"promemoriaGiorni",parseInt(v)||30)}
            onCancel={()=>setCellAttiva(null)}/>}
        </div>
      );
      // Campo data: usa input type=date (apre calendario) con nota formato
      if (col.key==="dataScadenza") return (
        <div>
          <CellaInline value={value} type="date"
            onSave={v=>aggiornaVoce(voce.id,col.key,v)}
            onCancel={()=>setCellAttiva(null)}/>
          <div style={{fontSize:10,color:"#9ca3af",marginTop:2}}>formato gg/mm/aaaa</div>
        </div>
      );
      const opts = col.special==="utente"
        ? [{value:"",label:"Nessuno"},...utenti.map(u=>({value:u.uid,label:u.displayName}))]
        : col.key==="nome" && opzioniNome ? opzioniNome
        : col.options||null;
      return (
        <CellaInline value={value} type={col.type||"text"} options={opts}
          onSave={v=>{
            if(col.special==="utente"){const u=utenti.find(u=>u.uid===v);aggiornaVoce(voce.id,"assegnatoA",v);aggiornaVoce(voce.id,"assegnatoNome",u?.displayName||"");}
            else aggiornaVoce(voce.id,col.key,v);
          }}
          onCancel={()=>setCellAttiva(null)}/>
      );
    }
    return <Cella value={value} display={display} canEdit={canEdit} onEdit={()=>setCellAttiva({id:voce.id,campo:col.key})}/>;
  };

  return (
    <div style={{padding:20}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10,flexWrap:"wrap",gap:8}}>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          {scadute>0&&<button onClick={()=>setFiltroScadenza(f=>f==="scadute"?"":"scadute")} style={{fontSize:11,fontWeight:700,color:"#dc2626",background:filtroScadenza==="scadute"?"#fca5a5":"#fee2e2",padding:"2px 8px",borderRadius:10,border:"1px solid "+(filtroScadenza==="scadute"?"#dc2626":"transparent"),cursor:"pointer"}}>⚠️ {scadute} {scadute===1?"scaduta":"scadute"}</button>}
          {urgenti>0&&<button onClick={()=>setFiltroScadenza(f=>f==="urgenti"?"":"urgenti")} style={{fontSize:11,fontWeight:700,color:"#d97706",background:filtroScadenza==="urgenti"?"#fdba74":"#fff7ed",padding:"2px 8px",borderRadius:10,border:"1px solid "+(filtroScadenza==="urgenti"?"#d97706":"transparent"),cursor:"pointer"}}>🔔 {urgenti} in scadenza</button>}
          <span style={{fontSize:12,color:"#9ca3af"}}>{voci.length} voci</span>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <div style={{position:"relative"}}>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍 Cerca..."
              style={{padding:"6px 26px 6px 10px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:12,fontFamily:"'Inter',sans-serif",outline:"none",width:150}}/>
            {search&&<button onClick={()=>setSearch("")} style={{position:"absolute",right:6,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",color:"#9ca3af",fontSize:14}}>×</button>}
          </div>
          {canEdit&&!predefiniteAggiunte&&vociPredefinite.length>0&&(
            <button onClick={aggiungiTuttePredefinite}
              style={{padding:"6px 12px",border:"1px solid #7c3aed",borderRadius:8,background:"#ede9fe",color:"#7c3aed",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
              ✦ Aggiungi predefinite
            </button>
          )}
          {canEdit&&(
            <button onClick={()=>aggiungiVoce()}
              style={{padding:"6px 12px",border:"none",borderRadius:8,background:"#7c3aed",color:"#fff",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",gap:4}}>
              <Plus size={13}/> Aggiungi riga
            </button>
          )}
        </div>
      </div>
      <div style={{fontSize:11,color:"#9ca3af",marginBottom:8}}>💡 Clicca su una cella per modificarla · ⠿ trascina per riordinare · ✓ per completare e rinnovare la scadenza</div>

      <div style={{overflowX:"auto",borderRadius:12,border:"1px solid #e5e7eb",WebkitOverflowScrolling:"touch"}}>
        <table style={{borderCollapse:"collapse",fontSize:12,minWidth:900,width:"100%"}}>
          <thead>
            <tr style={{background:"#f9fafb",borderBottom:"2px solid #e5e7eb"}}>
              <th style={{width:28,padding:"8px 6px"}}/>
              {COLONNE_BASE.map(col=>(
                <th key={col.key} style={{padding:"8px 10px",textAlign:"left",fontSize:11,fontWeight:700,color:"#6b7280",whiteSpace:"nowrap",minWidth:col.width}}>{col.label}</th>
              ))}
              <th style={{width:30}}/>
            </tr>
          </thead>
          <tbody>
            {filtrateVisibili.length===0 ? (
              <tr><td colSpan={COLONNE_BASE.length+2} style={{padding:40,textAlign:"center",color:"#9ca3af",fontSize:13}}>
                {voci.length===0 ? "Nessuna voce — usa i pulsanti in alto per aggiungere" : `Nessun risultato per "${search}"`}
              </td></tr>
            ) : filtrateVisibili.map((voce,ri)=>{
              const g = giorniAllaScadenza(voce.dataScadenza);
              const isScaduta = g!==null&&g<0;
              const isUrgente = g!==null&&g>=0&&g<=30;
              const isDragOver = dragOverIdx===ri;
              return (
                <tr key={voce.id} draggable onDragStart={e=>onDragStart(e,ri)} onDragOver={e=>onDragOver(e,ri)} onDrop={e=>onDrop(e,ri)} onDragEnd={()=>{setDragIdx(null);setDragOverIdx(null);}}
                  style={{borderBottom:"1px solid #f3f4f6",background:isDragOver?"#ede9fe":isScaduta?"#fff5f5":isUrgente?"#fffbeb":ri%2===0?"#fff":"#fafafa",
                    opacity:dragIdx===ri?0.4:1,transition:"background 0.1s"}}>
                  <td style={{padding:"4px 6px",cursor:"grab",color:"#d1d5db",textAlign:"center"}}>
                    <GripVertical size={14}/>
                  </td>
                  {COLONNE_BASE.map(col=>(
                    <td key={col.key} style={{padding:"4px 6px",verticalAlign:"middle"}}>
                      {renderCella(voce,col)}
                    </td>
                  ))}
                  <td style={{padding:"4px 6px"}}>
                    {canEdit&&<button onClick={()=>eliminaVoce(voce.id)}
                      style={{background:"none",border:"none",cursor:"pointer",color:"#d1d5db",padding:3}}
                      onMouseOver={e=>e.currentTarget.style.color="#dc2626"}
                      onMouseOut={e=>e.currentTarget.style.color="#d1d5db"}>
                      <X size={12}/>
                    </button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── TAB CONTRATTI DI MANUTENZIONE ──────────────────────────────────────
function ContrattiTab({ orgId, utenti, canEdit }) {
  const { addToast } = useToast();
  const [contratti, setContratti] = useState([]);
  const [cellAttiva, setCellAttiva] = useState(null);

  useEffect(()=>{
    if(!orgId) return;
    const q = query(collection(db,"scadenzario"), where("organizationId","==",orgId), where("tipo","==","contratto"));
    return onSnapshot(q,snap=>{
      const l=snap.docs.map(d=>({id:d.id,...d.data()}));
      l.sort((a,b)=>(a.ordine??999)-(b.ordine??999));
      setContratti(l);
    });
  },[orgId]);

  const aggiorna = async (id,campo,valore) => {
    await updateDoc(doc(db,"scadenzario",id),{[campo]:valore,updatedAt:serverTimestamp()});
    setCellAttiva(null);
  };

  const aggiungi = async () => {
    await addDoc(collection(db,"scadenzario"),{
      nome:"Nuovo contratto", tipo:"contratto", organizationId:orgId,
      dataInizio:"", dataDisdetta:"", dataFine:"",
      assegnatoA:"", assegnatoNome:"", note:"",
      promemoria:true, promemoriaGiorni:60,
      ordine:contratti.length, createdAt:serverTimestamp(), updatedAt:serverTimestamp(),
    });
  };

  const elimina = async id=>{ if (!await confirm("Eliminare?", { type:"danger" })) return; await deleteDoc(doc(db,"scadenzario",id)); };

  const COLONNE = [
    {key:"nome",          label:"Descrizione contratto",  width:200},
    {key:"assegnatoA",    label:"Assegnato a",            width:130, special:"utente"},
    {key:"dataInizio",    label:"Data inizio",            width:110, type:"date"},
    {key:"dataDisdetta",  label:"Data disdetta",          width:120, type:"date"},
    {key:"dataFine",      label:"Data fine",              width:110, type:"date"},
    {key:"__giorni",      label:"Giorni al termine",      width:120, special:"giorni"},
    {key:"promemoria",    label:"Promemoria email",       width:120, special:"promemoria"},
    {key:"note",          label:"Note",                   width:160},
  ];

  return (
    <div style={{padding:20}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <span style={{fontSize:13,color:"#9ca3af"}}>💡 Rosso se mancano meno di 60 giorni alla fine del contratto</span>
        {canEdit&&<button onClick={aggiungi} style={{padding:"6px 12px",border:"none",borderRadius:8,background:"#7c3aed",color:"#fff",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",gap:4}}>
          <Plus size={13}/> Aggiungi contratto
        </button>}
      </div>
      <div style={{overflowX:"auto",borderRadius:12,border:"1px solid #e5e7eb"}}>
        <table style={{borderCollapse:"collapse",fontSize:12,minWidth:900,width:"100%"}}>
          <thead>
            <tr style={{background:"#f9fafb",borderBottom:"2px solid #e5e7eb"}}>
              {COLONNE.map(col=><th key={col.key} style={{padding:"8px 10px",textAlign:"left",fontSize:11,fontWeight:700,color:"#6b7280",whiteSpace:"nowrap",minWidth:col.width}}>{col.label}</th>)}
              <th style={{width:30}}/>
            </tr>
          </thead>
          <tbody>
            {contratti.length===0?<tr><td colSpan={COLONNE.length+1} style={{padding:40,textAlign:"center",color:"#9ca3af"}}>Nessun contratto</td></tr>
            :contratti.map((c,ri)=>{
              const g = c.dataFine?giorniAllaScadenza(c.dataFine):null;
              const isUrgente = g!==null&&g<60;
              return (
                <tr key={c.id} style={{borderBottom:"1px solid #f3f4f6",background:isUrgente?"#fff5f5":ri%2===0?"#fff":"#fafafa"}}>
                  {COLONNE.map(col=>{
                    const isAttiva=cellAttiva?.id===c.id&&cellAttiva?.campo===col.key;
                    if(col.special==="giorni"){
                      const gg=c.dataFine?giorniAllaScadenza(c.dataFine):null;
                      return <td key={col.key} style={{padding:"4px 6px"}}>
                        {gg!==null?<span style={{fontWeight:700,color:gg<60?"#dc2626":"#059669"}}>{gg<0?`Scaduto da ${Math.abs(gg)}gg`:`${gg} gg`}</span>:<span style={{color:"#d1d5db"}}>—</span>}
                      </td>;
                    }
                    if(col.special==="promemoria"){
                      // Il promemoria dei contratti riguarda il termine di disdetta (o la fine, se manca)
                      return <td key={col.key} style={{padding:"4px 6px"}}>
                        <div style={{display:"flex",alignItems:"center",gap:5}}>
                          <input type="checkbox" disabled={!canEdit} checked={c.promemoria!==false} onChange={e=>aggiorna(c.id,"promemoria",e.target.checked)} style={{accentColor:"#7c3aed",width:14,height:14}} title="Email di promemoria prima del termine di disdetta"/>
                          {c.promemoria!==false && <input type="number" min="0" disabled={!canEdit} value={c.promemoriaGiorni??60}
                            onChange={e=>aggiorna(c.id,"promemoriaGiorni",Math.max(0,parseInt(e.target.value)||0))}
                            style={{width:52,padding:"3px 6px",border:"1px solid #e5e7eb",borderRadius:6,fontSize:11}} title="Giorni di preavviso"/>}
                          {c.promemoria!==false && <span style={{fontSize:10,color:"#9ca3af"}}>gg</span>}
                        </div>
                      </td>;
                    }
                    const opts=col.special==="utente"?[{value:"",label:"Nessuno"},...utenti.map(u=>({value:u.uid,label:u.displayName}))]:(col.options||null);
                    let display=null,value=c[col.key]||"";
                    if(col.type==="date"&&c[col.key]) display=new Date(c[col.key]+"T00:00:00").toLocaleDateString("it-IT");
                    if(col.special==="utente"){display=c.assegnatoNome?<span style={{fontSize:11,color:"#6b7280"}}>👤 {c.assegnatoNome}</span>:null;value=c.assegnatoNome||"";}
                    return (
                      <td key={col.key} style={{padding:"4px 6px",verticalAlign:"middle"}}>
                        {isAttiva?<CellaInline value={value} type={col.type||"text"} options={opts}
                          onSave={v=>{if(col.special==="utente"){const u=utenti.find(u=>u.uid===v);aggiorna(c.id,"assegnatoA",v);aggiorna(c.id,"assegnatoNome",u?.displayName||"");}else aggiorna(c.id,col.key,v);}}
                          onCancel={()=>setCellAttiva(null)}/>
                        :<Cella value={value} display={display} canEdit={canEdit} onEdit={()=>setCellAttiva({id:c.id,campo:col.key})}/>}
                      </td>
                    );
                  })}
                  <td style={{padding:"4px 6px"}}>{canEdit&&<button onClick={()=>elimina(c.id)} style={{background:"none",border:"none",cursor:"pointer",color:"#d1d5db",padding:3}} onMouseOver={e=>e.currentTarget.style.color="#dc2626"} onMouseOut={e=>e.currentTarget.style.color="#d1d5db"}><X size={12}/></button>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── TAB SCADENZE PERSONALIZZATE ────────────────────────────────────────
function PersonalizzateTab({ orgId, utenti, canEdit }) {
  const { addToast } = useToast();
  const [categorie, setCategorie] = useState([]);
  const [nuovaCategoria, setNuovaCategoria] = useState("");
  const [catAperta, setCatAperta] = useState(null);

  // Categorie personalizzate = record con tipo="cat_custom" e nome=categoria
  const [vociCustom, setVociCustom] = useState([]);

  useEffect(()=>{
    if(!orgId) return;
    const q1=query(collection(db,"scadenzario"),where("organizationId","==",orgId),where("tipo","==","cat_custom"));
    const q2=query(collection(db,"scadenzario"),where("organizationId","==",orgId),where("tipo","==","voce_custom"));
    const u1=onSnapshot(q1,snap=>setCategorie(snap.docs.map(d=>({id:d.id,...d.data()}))));
    const u2=onSnapshot(q2,snap=>setVociCustom(snap.docs.map(d=>({id:d.id,...d.data()}))));
    return ()=>{u1();u2();};
  },[orgId]);

  const aggiungiCategoria = async () => {
    if(!nuovaCategoria.trim()){addToast("Nome categoria obbligatorio","error");return;}
    await addDoc(collection(db,"scadenzario"),{nome:nuovaCategoria.trim(),tipo:"cat_custom",organizationId:orgId,createdAt:serverTimestamp()});
    setNuovaCategoria("");
  };

  const aggiungiVoce = async (categoriaId, categoriaNome) => {
    await addDoc(collection(db,"scadenzario"),{
      nome:"Nuova voce", tipo:"voce_custom", categoriaId, categoriaNome,
      organizationId:orgId, dataScadenza:"", ricorrenza:"Nessuna",
      promemoria:true, promemoriaGiorni:30, assegnatoA:"", assegnatoNome:"", note:"",
      createdAt:serverTimestamp(), updatedAt:serverTimestamp(),
    });
  };

  const aggiornaVoce = async (id,campo,valore) => {
    await updateDoc(doc(db,"scadenzario",id),{[campo]:valore,updatedAt:serverTimestamp()});
  };

  const eliminaCategoria = async id => {
    if (!await confirm("Eliminare categoria e tutte le sue voci?", { type:"danger" })) return;
    await deleteDoc(doc(db,"scadenzario",id));
    const vDaCancellare = vociCustom.filter(v=>v.categoriaId===id);
    for(const v of vDaCancellare) await deleteDoc(doc(db,"scadenzario",v.id));
  };

  return (
    <div style={{padding:20}}>
      <div style={{fontSize:13,color:"#6b7280",marginBottom:16}}>
        Crea le tue categorie personalizzate (Marketing, Commerciale, Qualità, Produzione…) e aggiungi le voci per ciascuna.
      </div>
      {canEdit&&(
        <div style={{display:"flex",gap:8,marginBottom:20}}>
          <input value={nuovaCategoria} onChange={e=>setNuovaCategoria(e.target.value)}
            placeholder="Nome nuova categoria..."
            onKeyDown={e=>e.key==="Enter"&&aggiungiCategoria()}
            style={{padding:"8px 12px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:13,fontFamily:"'Inter',sans-serif",outline:"none",flex:1,maxWidth:300}}/>
          <button onClick={aggiungiCategoria} style={{padding:"8px 16px",border:"none",borderRadius:8,background:"#7c3aed",color:"#fff",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
            + Crea categoria
          </button>
        </div>
      )}
      {categorie.length===0?<div style={{textAlign:"center",padding:48,color:"#9ca3af"}}>Nessuna categoria personalizzata — creane una</div>
      :categorie.map(cat=>{
        const voci=vociCustom.filter(v=>v.categoriaId===cat.id);
        return (
          <div key={cat.id} style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:12,marginBottom:16,overflow:"hidden"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 16px",background:"#f9fafb",borderBottom:"1px solid #e5e7eb"}}>
              <span style={{fontSize:14,fontWeight:700,color:"#111827"}}>📁 {cat.nome}</span>
              <div style={{display:"flex",gap:8}}>
                {canEdit&&<button onClick={()=>aggiungiVoce(cat.id,cat.nome)} style={{padding:"4px 10px",border:"1px solid #7c3aed",borderRadius:6,background:"#ede9fe",color:"#7c3aed",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>+ Aggiungi voce</button>}
                {canEdit&&<button onClick={()=>eliminaCategoria(cat.id)} style={{padding:"4px 8px",border:"none",background:"none",color:"#d1d5db",cursor:"pointer"}} onMouseOver={e=>e.currentTarget.style.color="#dc2626"} onMouseOut={e=>e.currentTarget.style.color="#d1d5db"}><X size={13}/></button>}
              </div>
            </div>
            <div style={{padding:12}}>
              {voci.length===0?<div style={{color:"#9ca3af",fontSize:12,padding:"8px 0"}}>Nessuna voce — clicca "+ Aggiungi voce"</div>
              :voci.map((v,ri)=>(
                <div key={v.id} style={{display:"grid",gridTemplateColumns:"1.5fr 130px 130px 100px 80px 110px 1fr auto",gap:6,alignItems:"center",padding:"6px 0",borderBottom:"1px solid #f3f4f6"}}>
                  <input value={v.nome} onChange={e=>aggiornaVoce(v.id,"nome",e.target.value)} style={{padding:"4px 8px",border:"1px solid #e5e7eb",borderRadius:6,fontSize:12,fontFamily:"'Inter',sans-serif",outline:"none"}}/>
                  <input type="date" value={v.dataScadenza||""} onChange={e=>aggiornaVoce(v.id,"dataScadenza",e.target.value)} style={{padding:"4px 8px",border:"1px solid #e5e7eb",borderRadius:6,fontSize:11,fontFamily:"'Inter',sans-serif",outline:"none"}}/>
                  <select value={v.ricorrenza||"Nessuna"} onChange={e=>aggiornaVoce(v.id,"ricorrenza",e.target.value)} style={{padding:"4px 8px",border:"1px solid #e5e7eb",borderRadius:6,fontSize:11,fontFamily:"'Inter',sans-serif",outline:"none"}}>
                    {RICORRENZE.map(r=><option key={r} value={r}>{r}</option>)}
                  </select>
                  <select value={v.assegnatoA||""} onChange={e=>{const u=utenti.find(u=>u.uid===e.target.value);aggiornaVoce(v.id,"assegnatoA",e.target.value);aggiornaVoce(v.id,"assegnatoNome",u?.displayName||"");}} style={{padding:"4px 8px",border:"1px solid #e5e7eb",borderRadius:6,fontSize:11,fontFamily:"'Inter',sans-serif",outline:"none"}}>
                    <option value="">Nessuno</option>
                    {utenti.map(u=><option key={u.uid} value={u.uid}>{u.displayName}</option>)}
                  </select>
                  <BadgeScadenza data={v.dataScadenza}/>
                  <div style={{display:"flex",alignItems:"center",gap:4}} title="Promemoria via email: giorni di preavviso">
                    <input type="checkbox" checked={!!v.promemoria} onChange={e=>aggiornaVoce(v.id,"promemoria",e.target.checked)} style={{accentColor:"#7c3aed",width:14,height:14}}/>
                    {v.promemoria && <><input type="number" min="0" value={v.promemoriaGiorni??30} onChange={e=>aggiornaVoce(v.id,"promemoriaGiorni",Math.max(0,parseInt(e.target.value)||0))}
                      style={{width:46,padding:"3px 5px",border:"1px solid #e5e7eb",borderRadius:6,fontSize:11}}/><span style={{fontSize:10,color:"#9ca3af"}}>gg 🔔</span></>}
                  </div>
                  <input value={v.note||""} onChange={e=>aggiornaVoce(v.id,"note",e.target.value)} placeholder="Note..." style={{padding:"4px 8px",border:"1px solid #e5e7eb",borderRadius:6,fontSize:11,fontFamily:"'Inter',sans-serif",outline:"none"}}/>
                  <button onClick={async()=>{if (await confirm("Eliminare?", { type:"danger" }))await deleteDoc(doc(db,"scadenzario",v.id));}} style={{background:"none",border:"none",cursor:"pointer",color:"#d1d5db",padding:3}} onMouseOver={e=>e.currentTarget.style.color="#dc2626"} onMouseOut={e=>e.currentTarget.style.color="#d1d5db"}><X size={12}/></button>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── PAGINA PRINCIPALE ───────────────────────────────────────────────────
// ── Scadenze di Manutenzione — calcolate dal vivo dai piani, mai una copia
// separata che rischia di disallinearsi da quanto già gestito in ogni singola
// commessa. Sola lettura qui: per intervenire davvero si va alla commessa giusta.
function ScadenzeManutenzioneTab({ orgId, onNavigate }) {
  const [piani, setPiani] = useState([]);
  const [interventi, setInterventi] = useState([]);
  const [impianti, setImpianti] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filtroScadenza, setFiltroScadenza] = useState("");
  const [vista, setVista] = useState("lista"); // lista | calendario
  const [meseCal, setMeseCal] = useState(() => { const d=new Date(); d.setDate(1); return d; });

  useEffect(()=>{
    if (!orgId) return;
    const u1 = onSnapshot(query(collection(db,"manutenzionePiani"), where("organizationId","==",orgId)),
      snap=>setPiani(snap.docs.map(d=>({id:d.id,...d.data()}))));
    const u2 = onSnapshot(query(collection(db,"manutenzioneInterventi"), where("organizationId","==",orgId)),
      snap=>setInterventi(snap.docs.map(d=>({id:d.id,...d.data()}))));
    const u3 = onSnapshot(query(collection(db,"impianti"), where("organizationId","==",orgId)),
      snap=>setImpianti(snap.docs.map(d=>({id:d.id,...d.data()}))));
    const u4 = onSnapshot(query(collection(db,"projects"), where("organizationId","==",orgId), where("isParent","==",false)),
      snap=>setProjects(snap.docs.map(d=>({id:d.id,...d.data()}))));
    return ()=>{u1();u2();u3();u4();};
  },[orgId]);

  // Una riga per VOCE (piani a capitolato — ognuna con la propria scadenza, la vista che
  // conta per pianificare davvero chi manda dove) più una riga per i piani ad albero
  // componenti (che non hanno granularità per singola voce, restano un'unica scadenza).
  const oggi = new Date(); oggi.setHours(0,0,0,0);
  const righe = piani.flatMap(p=>{
    const interventiPiano = interventi.filter(i=>i.pianoId===p.id).sort((a,b)=>(b.data||"").localeCompare(a.data||""));
    const impianto = impianti.find(i=>i.id===p.impiantoId);
    const project = projects.find(pr=>pr.id===(impianto?.projectId||p.projectId));
    if (!project) return [];
    if (p.modalita==="capitolato") {
      return (p.voci||[]).filter(v=>!v.np).map(v=>{ // voci N.P.: nessuna scadenza
        const scadenza = prossimaScadenzaVoce(v, p, interventiPiano);
        const giorni = Math.ceil((scadenza-oggi)/(1000*60*60*24));
        return { key:`${p.id}_${v.id}`, titolo:v.testo, piano:p, impianto, project, giorni, scadenza };
      });
    }
    const ultimo = interventiPiano[0]||null;
    const scadenza = prossimaScadenza(p, ultimo);
    if (!scadenza) return [];
    const giorni = Math.ceil((scadenza-oggi)/(1000*60*60*24));
    return [{ key:p.id, titolo:p.nome, piano:p, impianto, project, giorni, scadenza }];
  }).sort((a,b)=>a.giorni-b.giorni);

  const scadute = righe.filter(r=>r.giorni<0).length;
  const urgenti = righe.filter(r=>r.giorni>=0 && r.giorni<=14).length;
  const visibili = righe.filter(r=>{
    if (filtroScadenza==="scadute") return r.giorni<0;
    if (filtroScadenza==="urgenti") return r.giorni>=0 && r.giorni<=14;
    return true;
  });

  return (
    <div style={{padding:16}}>
      <div style={{display:"flex",gap:8,marginBottom:14,alignItems:"center"}}>
        {scadute>0 && <button onClick={()=>setFiltroScadenza(f=>f==="scadute"?"":"scadute")} style={{fontSize:11,fontWeight:700,color:"#dc2626",background:filtroScadenza==="scadute"?"#fca5a5":"#fee2e2",padding:"4px 10px",borderRadius:10,border:"1px solid "+(filtroScadenza==="scadute"?"#dc2626":"transparent"),cursor:"pointer"}}>⚠️ {scadute} {scadute===1?"scaduta":"scadute"}</button>}
        {urgenti>0 && <button onClick={()=>setFiltroScadenza(f=>f==="urgenti"?"":"urgenti")} style={{fontSize:11,fontWeight:700,color:"#d97706",background:filtroScadenza==="urgenti"?"#fde68a":"#fef3c7",padding:"4px 10px",borderRadius:10,border:"1px solid "+(filtroScadenza==="urgenti"?"#d97706":"transparent"),cursor:"pointer"}}>🔔 {urgenti} entro 14 giorni</button>}
        {righe.length===0 && <div style={{fontSize:13,color:"#9ca3af"}}>Nessuna scadenza di manutenzione nell'organizzazione.</div>}
        <div style={{marginLeft:"auto",display:"flex",gap:4,background:"#f3f4f6",borderRadius:8,padding:3}}>
          <button onClick={()=>setVista("lista")} style={{fontSize:11,fontWeight:600,padding:"5px 12px",borderRadius:6,border:"none",cursor:"pointer",background:vista==="lista"?"#fff":"transparent",color:vista==="lista"?"#7c3aed":"#6b7280",boxShadow:vista==="lista"?"0 1px 2px rgba(0,0,0,.08)":"none"}}>📋 Lista</button>
          <button onClick={()=>setVista("calendario")} style={{fontSize:11,fontWeight:600,padding:"5px 12px",borderRadius:6,border:"none",cursor:"pointer",background:vista==="calendario"?"#fff":"transparent",color:vista==="calendario"?"#7c3aed":"#6b7280",boxShadow:vista==="calendario"?"0 1px 2px rgba(0,0,0,.08)":"none"}}>📅 Calendario</button>
        </div>
      </div>

      {vista==="lista" && (
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {visibili.map(r=>{
            const colore = r.giorni<0 ? "#dc2626" : r.giorni<=14 ? "#d97706" : "#059669";
            const bg = r.giorni<0 ? "#fef2f2" : r.giorni<=14 ? "#fffbeb" : "#f0fdf4";
            return (
              <div key={r.key} onClick={()=>onNavigate&&onNavigate({page:"project",projectId:r.project.id,tab:"manutenzione"})}
                style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 14px",background:bg,border:`1px solid ${colore}22`,borderRadius:8,cursor:"pointer"}}>
                <div>
                  <div style={{fontSize:13,fontWeight:700,color:"#111827"}}>{r.titolo}</div>
                  <div style={{fontSize:11,color:"#6b7280",marginTop:2}}>{r.impianto?.nome||"—"} · {r.project.name}{r.piano.modalita==="capitolato"?` · 📋 ${r.piano.nome}`:""}</div>
                </div>
                <div style={{textAlign:"right",flexShrink:0}}>
                  <div style={{fontSize:12,fontWeight:700,color:colore}}>{r.giorni<0?`Scaduta da ${Math.abs(r.giorni)}gg`:`tra ${r.giorni}gg`}</div>
                  <div style={{fontSize:10,color:"#9ca3af"}}>{r.scadenza.toLocaleDateString("it-IT")}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {vista==="calendario" && (
        <CalendarioManutenzioni righe={righe} mese={meseCal} setMese={setMeseCal} onNavigate={onNavigate}/>
      )}
    </div>
  );
}

// ─── Calendario mensile — per pianificare i giri tecnici in anticipo, non solo
// reagire a quello che è già scaduto: "questa settimana ho 6 manutenzioni, chi mando
// dove". Ogni giorno mostra quante scadenze cadono lì, cliccabile per il dettaglio.
function CalendarioManutenzioni({ righe, mese, setMese, onNavigate }) {
  const [giornoSel, setGiornoSel] = useState(null);
  const anno = mese.getFullYear(), meseIdx = mese.getMonth();
  const primoGiorno = new Date(anno, meseIdx, 1);
  const ultimoGiorno = new Date(anno, meseIdx+1, 0);
  const primaColonna = (primoGiorno.getDay()+6)%7; // lunedì=0
  const giorni = [];
  for (let i=0;i<primaColonna;i++) giorni.push(null);
  for (let d=1; d<=ultimoGiorno.getDate(); d++) giorni.push(new Date(anno,meseIdx,d));

  const perGiorno = {};
  righe.forEach(r=>{
    const k = toLocalDateStr(r.scadenza);
    if (!perGiorno[k]) perGiorno[k]=[];
    perGiorno[k].push(r);
  });

  const oggiStr = toLocalDateStr(new Date());
  const nomiMesi = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];
  const righeGiornoSel = giornoSel ? (perGiorno[toLocalDateStr(giornoSel)]||[]) : [];

  return (
    <div>
      <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:16,marginBottom:12}}>
        <button onClick={()=>{const d=new Date(mese);d.setMonth(d.getMonth()-1);setMese(d);setGiornoSel(null);}} style={{background:"none",border:"none",cursor:"pointer",fontSize:16,color:"#7c3aed"}}>‹</button>
        <div style={{fontSize:14,fontWeight:700,minWidth:160,textAlign:"center"}}>{nomiMesi[meseIdx]} {anno}</div>
        <button onClick={()=>{const d=new Date(mese);d.setMonth(d.getMonth()+1);setMese(d);setGiornoSel(null);}} style={{background:"none",border:"none",cursor:"pointer",fontSize:16,color:"#7c3aed"}}>›</button>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4,marginBottom:4}}>
        {["Lun","Mar","Mer","Gio","Ven","Sab","Dom"].map(g=><div key={g} style={{fontSize:10,fontWeight:700,color:"#9ca3af",textAlign:"center",padding:4}}>{g}</div>)}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4}}>
        {giorni.map((g,i)=>{
          if (!g) return <div key={i}/>;
          const k = toLocalDateStr(g);
          const voci = perGiorno[k]||[];
          const isOggi = k===oggiStr;
          const isPassato = k<oggiStr;
          const sel = giornoSel && toLocalDateStr(giornoSel)===k;
          return (
            <div key={i} onClick={()=>voci.length>0 && setGiornoSel(g)}
              style={{minHeight:56,padding:6,borderRadius:7,border:sel?"2px solid #7c3aed":"1px solid #e5e7eb",
                background:isOggi?"#f4f0fc":"#fff",cursor:voci.length>0?"pointer":"default",opacity:isPassato&&voci.length===0?0.5:1}}>
              <div style={{fontSize:11,fontWeight:isOggi?800:600,color:isOggi?"#7c3aed":"#374151"}}>{g.getDate()}</div>
              {voci.length>0 && (
                <div style={{fontSize:9,fontWeight:700,color:"#fff",background:isPassato?"#dc2626":"#7c3aed",borderRadius:10,padding:"1px 6px",display:"inline-block",marginTop:3}}>
                  {voci.length}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {giornoSel && (
        <div style={{marginTop:16,padding:14,background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:8}}>
          <div style={{fontSize:12,fontWeight:700,color:"#7c3aed",marginBottom:8}}>{giornoSel.toLocaleDateString("it-IT",{weekday:"long",day:"numeric",month:"long"})}</div>
          {righeGiornoSel.map(r=>(
            <div key={r.key} onClick={()=>onNavigate&&onNavigate({page:"project",projectId:r.project.id,tab:"manutenzione"})}
              style={{padding:"6px 10px",background:"#fff",borderRadius:6,marginBottom:4,cursor:"pointer",fontSize:12}}>
              <strong>{r.titolo}</strong> — {r.impianto?.nome||"—"} · {r.project.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ScadenzarioPage({ onNavigate }) {
  const { isOrgAdmin, isPlatformOwner } = useAuth();
  const canSee = useCanSee();
  const { effectiveOrgId } = useOrg();
  const { addToast } = useToast();
  const [activeTab, setActiveTab] = useState("sicurezza");
  // Impostazioni dell'organizzazione: servono per sapere se il modulo impianti/verifiche
  // è stato attivato — se non lo è, la scheda "Verifiche normative" non deve comparire.
  const [orgSettings, setOrgSettings] = useState(null);
  useEffect(()=>{
    if(!effectiveOrgId) return;
    return onSnapshot(doc(db,"organizations",effectiveOrgId), snap=>setOrgSettings(snap.exists()?snap.data():null));
  },[effectiveOrgId]);
  const [utenti, setUtenti] = useState([]);
  const [vociExport, setVociExport] = useState([]);
  const canEdit = isOrgAdmin || isPlatformOwner;

  useEffect(()=>{
    if(!effectiveOrgId) return;
    return onSnapshot(query(collection(db,"users"),where("organizationId","==",effectiveOrgId),where("enabled","==",true)),
      snap=>setUtenti(snap.docs.map(d=>d.data())));
  },[effectiveOrgId]);

  // Dati reali della tab attiva, solo per l'export Excel (i componenti tab gestiscono i propri dati internamente)
  useEffect(()=>{
    if(!effectiveOrgId) return;
    if(activeTab==="custom"||activeTab==="manutenzioni"){ setVociExport([]); return; } // export non disponibile: categorie personalizzate e manutenzioni (dati già live altrove)
    const tipoQuery = activeTab==="auto" ? "auto" : activeTab==="contratti" ? "contratto" : activeTab;
    return onSnapshot(query(collection(db,"scadenzario"),where("organizationId","==",effectiveOrgId),where("tipo","==",tipoQuery)),
      snap=>setVociExport(snap.docs.map(d=>({id:d.id,...d.data()}))));
  },[effectiveOrgId,activeTab]);

  const TABS = [
    {id:"auto",           label:"🚗 Parco Auto",         icon:<Car size={13}/>,     voci:VOCI_AUTO},
    {id:"contratti",      label:"📄 Contratti",           icon:<FileText size={13}/>,voci:[]},
    {id:"immobili",       label:"🏢 Immobili e sedi",     icon:<FileText size={13}/>,voci:VOCI_IMMOBILI,        hasPredefinite:true, ricorrenzaDefault:"Annuale"},
    {id:"licenze",        label:"💻 Licenze",             icon:<Monitor size={13}/>, voci:VOCI_LICENZE,         hasPredefinite:true},
    {id:"amministrazione",label:"💰 Amministrazione",     icon:<FileText size={13}/>,voci:VOCI_AMMINISTRAZIONE,  hasPredefinite:true, ricorrenzaDefault:"Annuale"},
    {id:"personale",      label:"👤 Personale",           icon:<Users size={13}/>,   voci:VOCI_PERSONALE,       hasPredefinite:true},
    {id:"sicurezza",      label:"🛡️ Sicurezza",           icon:<Shield size={13}/>,  voci:VOCI_SICUREZZA,       hasPredefinite:true},
    {id:"contatori",      label:"⚡ Verifica Contatori",  icon:<Wrench size={13}/>,  voci:VOCI_CONTATORI,       hasPredefinite:true, ricorrenzaDefault:"Triennale"},
    {id:"spg",            label:"⚡ Tarature SPG",        icon:<Wrench size={13}/>,  voci:[],                   hasPredefinite:false, ricorrenzaDefault:"Quinquennale"},
    {id:"spi",            label:"🔥 Verifiche SPI",       icon:<Wrench size={13}/>,  voci:[],                   hasPredefinite:false, ricorrenzaDefault:"Quinquennale"},
    {id:"manutenzioni",   label:"🔧 Manutenzioni",       icon:<Wrench size={13}/>, voci:[],                    speciale:"manutenzioni"},
    ...((orgSettings?.impiantiVerificheAttivi && (isOrgAdmin || isPlatformOwner || canSee("verifiche-periodiche"))) ? [{id:"verifiche-normative", label:"🛡️ Verifiche normative", icon:<Shield size={13}/>, voci:[], speciale:"verifiche"}] : []),
    {id:"custom",         label:"⚙️ Personalizzate",      icon:<Settings size={13}/>,voci:[]},
  ];

  const tabCorrente = TABS.find(t=>t.id===activeTab)||TABS[0];

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>
      <div style={{background:"#fff",borderBottom:"1px solid #e5e7eb",padding:"14px 24px 0",flexShrink:0}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
        <button onClick={()=>{
          if(activeTab==="custom"||activeTab==="manutenzioni"){ addToast("Export non disponibile per questa scheda","warning"); return; }
          const intestazione=["Categoria","Descrizione","Data Scadenza","Giorni Rimanenti","Stato","Note"];
          const oggi=new Date();
          // Usa le voci reali della tab attiva
          const tutteFlat=(vociExport||[]);
          const righe=tutteFlat.map(s=>{
            const dataScad=s.dataScadenza?new Date(s.dataScadenza+"T00:00:00"):null;
            const giorni=dataScad?Math.ceil((dataScad-oggi)/(1000*60*60*24)):null;
            return [
              tabCorrente?.label||"—", s.descrizione||s.nome||"—",
              dataScad?dataScad.toLocaleDateString("it-IT"):"—",
              giorni!==null?(giorni<0?`Scaduta da ${Math.abs(giorni)}gg`:`${giorni}gg`):"—",
              giorni!==null&&giorni<0?"Scaduta":giorni!==null&&giorni<30?"In scadenza":"OK",
              s.note||""
            ];
          });
          const nomeTab = (tabCorrente?.label||"Scadenzario").replace(/[^\w\s]/g,"").trim().replace(/\s+/g,"_");
          esportaXLSX([intestazione,...righe],`Scadenze_${nomeTab}_${new Date().toLocaleDateString("it-IT").replace(/\//g,"-")}`,"Scadenzario");
        }} style={{display:"flex",alignItems:"center",gap:5,padding:"7px 14px",border:"1px solid #059669",borderRadius:8,background:"#f0fdf4",color:"#059669",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
          📊 Export Excel
        </button>
        </div>
        <h2 style={{fontSize:18,fontWeight:700,display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
          <Clock size={18} color="#7c3aed"/> Scadenze & Ricorrenze
          <InsightButton onNavigate={onNavigate}/>
        </h2>
        <div style={{fontSize:11.5,color:"#0e7490",background:"#ecfeff",border:"1px solid #a5f3fc",borderRadius:8,padding:"6px 12px",marginBottom:10,lineHeight:1.5}}>
          🔔 <strong>Promemoria via email</strong>: per ogni voce con il promemoria attivo, ogni mattina alle 7 arriva un'email al <strong>titolare</strong> e alla <strong>persona assegnata</strong> quando mancano i giorni di preavviso impostati, e di nuovo il giorno della scadenza. Ogni avviso parte una sola volta per ciascuna data.
        </div>
        <div style={{display:"flex",gap:2,overflowX:"auto"}}>
          {TABS.map(t=>(
            <button key={t.id} onClick={()=>setActiveTab(t.id)}
              style={{padding:"7px 14px",borderRadius:"8px 8px 0 0",border:"1px solid #e5e7eb",
                borderBottom:activeTab===t.id?"1px solid #fff":"1px solid #e5e7eb",
                background:activeTab===t.id?"#fff":"#f9fafb",
                color:activeTab===t.id?"#7c3aed":"#6b7280",
                fontWeight:activeTab===t.id?700:400,cursor:"pointer",
                fontFamily:"'Inter',sans-serif",fontSize:12,whiteSpace:"nowrap"}}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{flex:1,overflowY:"auto"}}>
        {activeTab==="personale" && <div style={{padding:"16px 16px 0"}}><RichiamoScadenzeContrattuali onNavigate={onNavigate}/></div>}
        {activeTab==="verifiche-normative" ? <VerifichePeriodicheTab onNavigate={onNavigate}/>
        : activeTab==="manutenzioni" ? <ScadenzeManutenzioneTab orgId={effectiveOrgId} onNavigate={onNavigate}/>
        : activeTab==="contratti" ? <ContrattiTab orgId={effectiveOrgId} utenti={utenti} canEdit={canEdit}/>
        : activeTab==="custom" ? <PersonalizzateTab orgId={effectiveOrgId} utenti={utenti} canEdit={canEdit}/>
        : activeTab==="auto" ? <AutoTab orgId={effectiveOrgId} utenti={utenti} canEdit={canEdit}/>
        : <ScadenzarioTab key={activeTab} tipo={activeTab}
            vociPredefinite={tabCorrente.hasPredefinite!==false ? tabCorrente.voci : []}
            ricorrenzaDefault={tabCorrente.ricorrenzaDefault||"Nessuna"}
            orgId={effectiveOrgId} utenti={utenti} canEdit={canEdit}/>}
      </div>
    </div>
  );
}
